import * as $ from "jquery";
import * as _ from "lodash";
import { DashboardCategoryCollection } from "../../../collections/DashboardCategories";
import { DashboardChannelCollection } from "../../../collections/DashboardChannels";
import { DashboardWidgetCollection } from "../../../collections/DashboardWidgets";
import { ModuleCollection } from "../../../collections/Modules";
import { DashboardWidget } from "../../../models/DashboardWidget";
import { DashboardCategoryView } from "../../dashboard/DashboardCategoryView";
import { DashboardPackeryLayout } from "../../dashboard/DashboardPackeryLayout";
import { DashboardWidgetManager } from "../../dashboard/widgets/DashboardWidgetManager";
import { ModifyDashboardWidgetModal } from "../../modals/dashboard/modify_widgets/ModifyDashboardWidgetModal";
import { getProgressPropertiesFromStatus, pollReportTask } from "../ReportUtils";
import { EditReportsNavView } from "./EditReportsNavView";
import { BaseEditSubView, BaseEditSubViewOptions } from "./_BaseEditView";

declare var App;
declare var CONFIG;

interface DateOptions {
    dataRange: string;
    timezone: string;
    widget: DashboardWidget;
    channel: Backbone.Model;
    customStartTime: number;
    customEndTime: number;
    dailyTimeFrom: number;
    dailyTimeTo: number;
    channelFunction: string;
    widgetType: number;
}

export class EditPreviewView extends BaseEditSubView {

    private template = _.template(require('../../../templates/reports/editReports/editPreview.html'));
    private navView: EditReportsNavView;
    private report = this.options.report;
    private reportId = this.report.get('id');
    private dashboardCategoryCollection: DashboardCategoryCollection = null;
    private dashboardWidgetCollection: DashboardWidgetCollection = null;
    private dashboardChannelCollection: DashboardChannelCollection = null;
    private moduleCollections: { [key: number]: ModuleCollection };
    private categoryViews: DashboardCategoryView[] = [];
    private widgetManager: DashboardWidgetManager = null;
    private dashboardId = this.report.get('dashboard_id');
    private collectionLoaded = false;
    private previewEmpty = true;
    private validWidgets = [3, 9, 10, 11, 12, 13, 18];

    constructor(options: BaseEditSubViewOptions) {
        super(<any>_.extend(options, {
            events: {
                'click .add-widget': 'handleAddWidgetClick',
                'click .btn-success.download-report': 'handleDownloadReport',
                'click .w-confirm.form-control': 'handleWidgetAddedOrRemovedOrUpdated'
            }
        }));

        this.navView = new EditReportsNavView(options);

        this.resetView();
        this.listenTo(App.Bus, 'dashboard:setLoading', () => {
            this.setLoading();
        });

        this.listenTo(App.Bus, 'dashboard:config:changed', () => {
            if (this.dashboardWidgetCollection) {
                var empty = this.isEmpty();

                if (empty !== this.previewEmpty) {
                    this.previewEmpty = empty;
                    this.show(this.dashboardId);
                }
            }
            this.handleWidgetAddedOrRemovedOrUpdated();
        });
        this.show(this.dashboardId);
    }

    private resetView() {
        this.stopListening(this.dashboardWidgetCollection, 'sync');

        this.dashboardChannelCollection = null;
        this.moduleCollections = {};
        this.collectionLoaded = false;

        if (this.widgetManager) this.widgetManager.remove();
        this.widgetManager = null;
        while (this.categoryViews.length) this.categoryViews.pop().remove();

        this.dashboardChannelCollection = new DashboardChannelCollection([], {
            dashboardId: this.dashboardId
        });
        this.dashboardCategoryCollection = new DashboardCategoryCollection([], {
            dashboardId: this.dashboardId
        });
        this.dashboardWidgetCollection = new DashboardWidgetCollection([], {
            dashboardId: this.dashboardId
        });

        this.widgetManager = new DashboardWidgetManager({
            appRegistry: this.options.appRegistry,
            appCollection: this.options.appCollection,
            brandCollection: this.options.brandCollection,
            moduleCollections: this.moduleCollections,
            installCollection: this.options.installCollection,
            permissionCollection: this.options.permissionCollection,
            organizationCollection: this.options.organizationCollection,
            dashboardCollection: this.options.dashboardCollection,
            dashboardChannelCollection: this.dashboardChannelCollection,
            dashboardCategoryCollection: this.dashboardCategoryCollection,
            dashboardWidgetCollection: this.dashboardWidgetCollection,
            dashboardId: this.dashboardId,
            currentUser: this.options.currentUser,
            validWidgets: this.validWidgets,
            report: this.report
        });
    }

    private setLoading(loading?: boolean) {
        this.$('.loading').toggleClass('hidden', loading === false);
        this.$('.not-loading').toggleClass('hidden', loading !== false);
        this.$('.polling-task').addClass('hidden');

        if (!loading) {
            this.dashboardCategoryCollection.each((m) => {
                App.Bus.trigger(`dashboard:category:${m.get('id')}:layout`);
            });
        }
    }

    show(dashboardId) {
        this.dashboardId = dashboardId;

        // Reset the view
        this.resetView();
        this.setLoading();

        $.when(
            this.dashboardCategoryCollection.getCollectionFetchPromise(),
            this.dashboardChannelCollection.getCollectionFetchPromise(),
            this.dashboardWidgetCollection.getCollectionFetchPromise()
        ).done(() => {
            this.collectionLoaded = true;
            this.previewEmpty = this.isEmpty();
            this.render();

            this.listenTo(this.dashboardWidgetCollection, 'sync', () => {
                var newIsEmpty = this.isEmpty();
                if (this.previewEmpty !== newIsEmpty) {
                    this.previewEmpty = newIsEmpty;
                    this.render();
                }
            });
        });
    }

    private isEmpty() {
        return this.collectionLoaded && this.dashboardWidgetCollection.filter((widget) => {
            return widget.get("dashboard_widget_type_id") !== 4
        }).length < 1
    }

    render() {
        this.$el.html(this.template({
            report: this.report,
            orgId: this.options.orgId,
            collectionLoaded: this.collectionLoaded,
            previewEmpty: this.previewEmpty,
            reporterUrl: CONFIG.reporter.url
        }));

        this.renderSubViews();

        // Hide chart converted/raw data options for preview charts
        this.$('.form-group.chart-convert').addClass('hidden');
        return this;
    }

    private renderSubViews() {
        if (!this.collectionLoaded) return;

        var categoriesContainer = this.$('.body .categories');
        // Add category ids uniquely in order
        this.dashboardCategoryCollection.each((category) => {
            var view = new DashboardCategoryView({
                model: category,
                permissionCollection: this.options.permissionCollection,
                dashboardChannelCollection: this.dashboardChannelCollection,
                dashboardCategoryCollection: this.dashboardCategoryCollection,
                dashboardWidgetCollection: this.dashboardWidgetCollection,
                widgetManager: this.widgetManager,
                dashboardId: this.dashboardId
            });
            this.categoryViews.push(view);
            categoriesContainer.append(view.$el);
            view.render();
            view.setLayout(new DashboardPackeryLayout({
                permissionCollection: this.options.permissionCollection,
                model: category,
                $el: view.$el,
                maxColumns: 1
            }));

            // Category header is invisible
            this.$('.category .head').addClass('hidden');
        });

        this.renderNavBar();
    }

    private handleAddWidgetClick() {
        var view = new ModifyDashboardWidgetModal({
            appRegistry: this.options.appRegistry,
            appCollection: this.options.appCollection,
            brandCollection: this.options.brandCollection,
            installCollection: this.options.installCollection,
            permissionCollection: this.options.permissionCollection,
            organizationCollection: this.options.organizationCollection,
            dashboardCollection: this.options.dashboardCollection,
            dashboardChannelCollection: this.dashboardChannelCollection,
            dashboardCategoryCollection: this.dashboardCategoryCollection,
            dashboardWidgetCollection: this.dashboardWidgetCollection,
            dashboardId: this.report.get('dashboard_id'),
            moduleCollections: this.moduleCollections,
            currentUser: this.options.currentUser,
            widget: null,
            categoryId: this.$(".category:not(.minimal)").eq(0).data('category-id'),
            modelId: null,
            validWidgets: this.validWidgets,
            report: this.report
        });
        $('.modals').append(view.render().$el);
        view.show();
    }

    handleWidgetAddedOrRemovedOrUpdated() {
        this.savePreview();

        this.$('.download-report').toggleClass('hidden', !_.size(this.dashboardWidgetCollection));
    }

    private getUIOrderMap(category: Backbone.Model) {
        // Get the order of our widgets, as laid out in the UI by packery (this is different from the DOM order)
        try {
            var widgetPositions = JSON.parse(category.get('widget_ui_positions'))["1"];
            var rtn = {};
            _.each(widgetPositions, (v, i) => {
                rtn[v.attr] = i
            });
            return rtn;
        } catch (e) {
            return {}
        }
    }

    savePreview(e?: JQuery.UIEventBase) {
        var d = $.Deferred();
        if (e) e.preventDefault();

        // No longer need to save recipe - it's generated dynamically
        $.when(this.options.reportsCollection.getCollectionFetchPromise()).done(() => {
            d.resolve();
        });

        return d;
    }

    private handleDownloadReport(e: JQuery.UIEventBase) {
        const isCSVData = this.report.get('format') === 'csv_data';

        // For CSV Data reports, at least one recipient is needed to generate a file.
        if(isCSVData && !this.report.get('recipient')){
            // Notify the user
            (<any>$).notify({
                title: 'Missing recipient(s).',
                message: `Please select at least one recipient on the report settings page 
                    to receive an email notification when the file is ready.`,
                icon: 'fa fa-exclamation-circle',
            }, {
                animate: {
                    enter: 'animated fadeInUp',
                },
                // The type property defines the css style
                // See alerts.less for available styles
                type: "warning-alt",
                placement: { from: "bottom", align: "center" }
            });
            return;
        }

        this.setLoading();
        $.when(this.savePreview(e)).done(() => {
            $.ajax({
                type: "POST",
                url: CONFIG.reporter.url + '/report/' + this.reportId,
                dataType: 'json',
                contentType: "application/json; charset=utf-8",
                complete: (res) => {
                    // For csv_data reports we don't want to instantly 
                    // download the report since it could take too much time.
                    if(isCSVData){
                        // Notify the user
                        (<any>$).notify({
                            title: 'Your file is being generated',
                            message: `You will be notified via email once is ready for download.`,
                            icon: 'fa fa-check-circle',
                        }, {
                            animate: {
                                enter: 'animated fadeInUp',
                            },
                            // The type property defines the css style
                            // See alerts.less for available styles
                            type: "success-alt",
                            placement: { from: "bottom", align: "center" }
                        });

                        this.setLoading(false);
                    } else {
                        this.handlePollReportTask(res.responseJSON.taskId);
                    }
                }
            });
        });
    }

    private setPolling(status, error) {
        this.$('.loading').addClass('hidden');
        this.$('.polling-task').removeClass('hidden');
        this.$('.not-loading').addClass('hidden');

        const { width, step, success } = getProgressPropertiesFromStatus(status, error);
        this.$('.polling-task .progress-bar').toggleClass('progress-bar-success', success);
        this.$('.polling-task .progress-bar').toggleClass('progress-bar-danger', !success);
        this.$('.polling-task .progress-bar').css({ width: width + '%' });
        this.$('.polling-task .progress-bar').text(step);
    }

    private handlePollReportTask(taskId: number) {
        pollReportTask({
            taskId,
            report: this.report,
            updateStatusFn: (status, error) => {
                this.setPolling(status, error);
            },
            doneFn: () => {
                this.setLoading(false);
            }
        });
    }

    private renderNavBar() {
        this.$('.nav-container').empty().append(this.navView.render().el);
    }

    remove() {
        this.stopListening();
        this.navView.remove();
        while (this.categoryViews.length) this.categoryViews.pop().remove();
        return super.remove();
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/views/reports/editReports/EditPreviewView.ts